/**
 * User Management API Services
 * 
 * This file contains all API calls for user management operations.
 * Connected to AWS Lambda functions via API Gateway.
 * 
 * Backend:
 * - Lambda function: userManagement
 * - API Gateway endpoint: Configured via environmentConfig.LAMBDA_URL.USER_MANAGEMENT
 * - Cognito User Pool with custom attributes (role, status)
 * - Cognito Groups: Admins, Users
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { environmentConfig } from '../config/config';

const API_ENDPOINT = environmentConfig.LAMBDA_URL.USER_MANAGEMENT;

/**
 * Get all users from Cognito User Pool
 * @returns {Promise<Array>} List of users with their details
 */
export const getAllUsers = async () => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await fetch(`${API_ENDPOINT}/users`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Create a new user in Cognito User Pool
 * @param {Object} userData - User information
 * @param {string} userData.email - User email
 * @param {string} userData.firstName - User first name
 * @param {string} userData.lastName - User last name
 * @param {string} userData.role - User role (admin/user)
 * @returns {Promise<Object>} Created user details
 */
export const createUser = async (userData) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await fetch(`${API_ENDPOINT}/users`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userData)
    });
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Update user details in Cognito User Pool
 * @param {string} userId - User ID to update
 * @param {Object} userData - Updated user information
 * @returns {Promise<Object>} Updated user details
 */
export const updateUser = async (userId, userData) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await fetch(`${API_ENDPOINT}/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(userData)
    });
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

/**
 * Toggle user status (activate/deactivate)
 * @param {string} userId - User ID
 * @param {string} status - New status (active/inactive)
 * @returns {Promise<Object>} Updated user status
 */
export const updateUserStatus = async (userId, status) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await fetch(`${API_ENDPOINT}/users/${userId}/status`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status })
    });
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating user status:', error);
    throw error;
  }
};

/**
 * Get current user's role and permissions
 * @returns {Promise<Object>} User role and permissions
 */
export const getCurrentUserRole = async () => {
  try {
    const { fetchUserAttributes } = await import('aws-amplify/auth');
    const attributes = await fetchUserAttributes();
    return {
      role: attributes['custom:role'] || 'user',
      status: attributes['custom:status'] || 'active'
    };
  } catch (error) {
    console.error('Error fetching user role:', error);
    throw error;
  }
};

/**
 * Delete user from Cognito User Pool
 * @param {string} userId - User ID to delete
 * @returns {Promise<void>}
 */
export const deleteUser = async (userId) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await fetch(`${API_ENDPOINT}/users/${userId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Resend temporary password to user
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
export const resendTemporaryPassword = async (userId) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await fetch(`${API_ENDPOINT}/users/${userId}/reset-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error resending password:', error);
    throw error;
  }
};

/**
 * Resend invite email to user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Response data
 */
export const resendInviteEmail = async (userId) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    const response = await fetch(`${API_ENDPOINT}/users/${userId}/resendInviteEmail`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error resending invite email:', error);
    throw error;
  }
};
